/**
 * Grey placeholder shapes for the Export-DWG (CAD) steps.
 *
 * Every CAD step - Tags, Tins, Signals, Tracks, the Tables step and the rest -
 * shows the same component while its data is still being fetched. Before this file
 * existed they all asked for one id, 'station', which drew a single huge grey
 * rectangle: the same shape no matter what the step was about to show, and much
 * taller than most steps ever are.
 *
 * Each step now names its own skeleton instead, and describes it here as a few
 * numbers rather than as its own block of markup. That keeps one set of markup in
 * skeleton.component.html while still letting each step be tuned on its own - if
 * the Tins step grows a fourth track panel, only its `panels` number changes here.
 */

/** The three shapes a CAD step body takes on screen. */
export type CadSkeletonLayout =
  /** A plain grid of label + input pairs, such as Tracks or Adjacent Station. */
  | 'form'
  /** Collapsible panels holding repeated cards, such as Tins or Tags. */
  | 'accordion'
  /** One of the nine drawing tables, shown as a header row plus body rows. */
  | 'table';

export interface CadSkeletonSpec {
  layout: CadSkeletonLayout;

  /** Draw the step title and its right-hand controls above the body. */
  toolbar?: boolean;

  /** accordion: how many panel bars to draw. Only the first is drawn open. */
  panels?: number;

  /** accordion: how many cards to draw inside the open panel. */
  cards?: number;

  /**
   * form: how many rows of fields.
   * accordion: how many rows of fields inside one card.
   * table: how many body rows.
   */
  rows?: number;

  /** How many fields sit across one row (2, 3 or 4). */
  columns?: number;
}

/** Shape used for any table in the Tables step; they all draw the same grid. */
export const CAD_TABLE_SKELETON: CadSkeletonSpec = { layout: 'table', rows: 6, columns: 4 };

/**
 * One entry per CAD step, keyed by the id that step passes to <app-skeleton>.
 *
 * The numbers are meant to match what the step normally renders, so the grey shapes
 * are replaced by real content of roughly the same size and the page does not jump.
 */
export const CAD_SKELETON_SPECS: Record<string, CadSkeletonSpec> = {
  // ---- Flat forms: one grid of fields, no panels ----------------------------
  'adjacent-station-details': { layout: 'form', rows: 4, columns: 3 },

  // ---- Panelled steps: one panel per track / direction, cards inside --------
  tracks: { layout: 'accordion', panels: 1, cards: 2, rows: 3, columns: 4 },
  utilities: { layout: 'accordion', panels: 2, cards: 2, rows: 4, columns: 2 },
  tins: { layout: 'accordion', toolbar: true, panels: 3, cards: 2, rows: 2, columns: 3 },
  tags: { layout: 'accordion', toolbar: true, panels: 4, cards: 3, rows: 2, columns: 3 },
  'approach-tags': { layout: 'accordion', panels: 2, cards: 2, rows: 2, columns: 3 },
  signals: { layout: 'accordion', panels: 3, cards: 2, rows: 2, columns: 3 },
  turnouts: { layout: 'accordion', panels: 3, cards: 2, rows: 2, columns: 3 },
  'dead-ends': { layout: 'accordion', panels: 2, cards: 2, rows: 1, columns: 4 },
  'trap-points': { layout: 'accordion', panels: 2, cards: 2, rows: 2, columns: 3 },
  'kavach-territory': { layout: 'accordion', panels: 2, cards: 2, rows: 1, columns: 4 },
  'adjustment-point': { layout: 'accordion', panels: 2, cards: 1, rows: 1, columns: 4 },

  // Both of these sit below their own upload panel, which stays on screen while
  // this skeleton shows, so neither one draws a toolbar of its own.
  'gradient-plan': { layout: 'accordion', panels: 2, cards: 3, rows: 1, columns: 3 },
  psr: { layout: 'accordion', panels: 2, cards: 3, rows: 1, columns: 4 },
};

/**
 * Finds the skeleton for an id, or null when the id is not a CAD step.
 *
 * The Tables step does not pass a fixed name: it passes the key of whichever table
 * the user selected ('LEGEND_TABLE', 'IP_PORT_TABLE' and seven more). Listing all
 * nine here would mean editing this file every time a table is added, so any key
 * ending in _TABLE is answered with the shared table shape.
 */
export function resolveCadSkeleton(id: unknown): CadSkeletonSpec | null {
  if (typeof id !== 'string' || !id) return null;
  if (CAD_SKELETON_SPECS[id]) return CAD_SKELETON_SPECS[id];
  return id.endsWith('_TABLE') ? CAD_TABLE_SKELETON : null;
}
